import type { FieldRef } from "./types";

/** Operator wording, matching the condition builder's own labels. */
const OP_LABELS: Record<string, string> = {
  eq: "is",
  ne: "is not",
  in: "is one of",
  not_in: "is none of",
  contains: "includes",
  not_contains: "does not include",
  filled: "is answered",
  empty: "is blank",
  gt: "is more than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  between: "is between",
};

/**
 * The same comparison, said the way the field would say it.
 *
 * "'valid until' is more than 2026-01-01" is not wrong so much as unreadable,
 * and an unreadable rule is one nobody checks. Mirrors `_ORDER_WORDS` in
 * main/conditions.py and DATE_OP_LABELS in the builder — three places, because
 * the sentence is written on the client while the audit trail is written on
 * the server, and they have to agree.
 */
const DATE_OP_LABELS: Record<string, string> = {
  gt: "is after",
  gte: "is on or after",
  lt: "is before",
  lte: "is on or before",
  between: "is between",
};

const NO_VALUE = ["filled", "empty"];

const ORDER_OPS = ["gt", "gte", "lt", "lte", "between"];

const DATE_COMPONENTS = ["DatePicker", "DateRange", "TimePicker"];

const looksLikeADate = (value: unknown) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value.trim());

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * A stage's rule, in words.
 *
 * The API returns a `condition_text` for saved stages, but it is computed from
 * what is stored — so the moment someone edits a rule it describes the previous
 * one, and for a stage that has never been saved there is nothing at all. This
 * describes the rule in hand.
 *
 * Names are swapped for labels and stored values for their option labels, so
 * the sentence reads the way the person who wrote the form would say it:
 * "Location is one of Plant A, Plant B" rather than
 * "department in planta, plantb".
 */
export const describeCondition = (
  rule: any,
  fields: FieldRef[],
  t: Translate
): string => {
  const byName = new Map(fields.map((f) => [f.name, f]));

  const valueText = (fieldName: string, value: unknown): string => {
    const options = byName.get(fieldName)?.options ?? [];
    const label = (v: unknown) =>
      options.find((o) => String(o.value) === String(v))?.label ?? String(v);
    if (Array.isArray(value)) return value.map(label).join(", ");
    return label(value);
  };

  /** A value that names another field, rather than being one. */
  const refName = (value: any): string | null =>
    value && typeof value === "object" && typeof value.field === "string"
      ? byName.get(value.field)?.label || value.field
      : null;

  const ordered = (node: any, name: string): string => {
    const field = byName.get(node.field);
    const isDate =
      (field &&
        DATE_COMPONENTS.some((c) => (field.component || "").includes(c))) ||
      looksLikeADate(Array.isArray(node.value) ? node.value[0] : node.value);
    const word = (op: string) =>
      t((isDate ? DATE_OP_LABELS[op] : undefined) ?? OP_LABELS[op] ?? op);
    const side = (v: unknown) => refName(v) ?? String(v);

    if (node.op === "between") {
      const list = Array.isArray(node.value) ? node.value : [];
      if (list.length !== 2) return `${name} ${word("between")}`;
      return `${name} ${word("between")} ${side(list[0])} ${t("and")} ${side(list[1])}`;
    }
    // Comparing two fields has no literal to read a shape from, so it is said
    // neutrally rather than guessing "after" or "more than".
    const ref = refName(node.value);
    if (ref !== null) {
      const neutral: Record<string, string> = {
        gt: "is greater than",
        gte: "is greater than or equal to",
        lt: "is less than",
        lte: "is less than or equal to",
      };
      return `${name} ${t(neutral[node.op] ?? node.op)} ${ref}`;
    }
    return `${name} ${word(node.op)} ${side(node.value)}`;
  };

  const clause = (node: any): string => {
    const field = byName.get(node.field);
    const name = field?.label || node.field;
    if (ORDER_OPS.includes(node.op)) return ordered(node, name);
    const op = t(OP_LABELS[node.op] ?? node.op);
    if (NO_VALUE.includes(node.op)) return `${name} ${op}`;
    return `${name} ${op} ${valueText(node.field, node.value)}`;
  };

  /**
   * `outer` is the joiner of the group this node sits in, so a nested group
   * can tell whether it needs bracketing.
   *
   * Without this, `all[ any[a, b], c ]` reads "a or b and c" — which is not
   * what it means and, worse, is a perfectly plausible sentence, so nobody
   * doubts it. Found by describing a real branchy process: a management
   * approval rule of "(critical OR permanent) AND affects safety systems"
   * came out as "is Critical or is Permanent and is Yes".
   */
  const walk = (node: any, outer?: "all" | "any"): string => {
    if (!node || typeof node !== "object") return "";
    if (typeof node.field === "string") return clause(node);

    if (node.not) {
      const inner = walk(node.not);
      return inner ? t("not ({{rule}})", { rule: inner }) : "";
    }

    for (const join of ["all", "any"] as const) {
      const list = node[join];
      if (Array.isArray(list)) {
        const parts = list.map((child: any) => walk(child, join)).filter(Boolean);
        if (parts.length === 0) return "";
        if (parts.length === 1) return parts[0];
        const joined = parts.join(join === "all" ? t(" and ") : t(" or "));
        // Bracket only where the grouping is actually ambiguous: a group
        // nested in a group that joins the same way ("a and (b and c)")
        // means exactly what the flat reading says, and the brackets would be
        // noise on the commonest rules.
        return outer && outer !== join ? `(${joined})` : joined;
      }
    }
    return "";
  };

  return walk(rule);
};

/** True when a stage does not always run. */
export const isConditional = (rule: any): boolean =>
  !!rule && typeof rule === "object" && Object.keys(rule).length > 0;
